import { pr, rcLogger, timeCall } from '@readcloud/common';
import { ErrorType, PDFData } from '@readcloud/data';
import axios from 'axios';
import { CustomPDFDataRangeTransport } from './CustomPDFDataRangeTransport';
import { getBookTypeFromMime, getType } from './documentUtils';

const customRangeHeaders = {
  range: 'x-range',
  contentRange: 'x-content-range',
};

const processChunk = async ({
  data,
  counter,
  isbn,
  minor,
  longId,
  onError,
}: {
  data: ArrayBuffer;
  counter: number;
  onError: (error: ErrorType) => void;
  isbn?: string;
  minor?: number;
  longId?: string;
}): Promise<ArrayBuffer | Uint8Array> => {
  rcLogger.debug('Minor', minor);
  switch (`${minor}`) {
    case '2':
      rcLogger.debug('Beggining method = 2 RCS Process');
      if (!longId || !isbn) {
        throw new Error('Invalid instantiation parameters');
      }
      try {
        const enhancedData: ArrayBuffer | Uint8Array | { error: any } =
          await timeCall('RCS Process')(() =>
            isbn && longId && minor
              ? pr(isbn, data, longId, minor, counter)
              : Promise.reject(new Error('Invalid instantiation parameters'))
          );
        if ('error' in enhancedData) {
          throw enhancedData.error;
        }
        rcLogger.debug('RCS Process Finished');
        return enhancedData;
      } catch (e) {
        rcLogger.debug('RCS Process failed');
        onError({ error: e as Error, type: 'ProcessError' });
        return data;
      }

    default:
      return data;
  }
};

export const prepareWebDocument = async (props: {
  fileUrl: string;
  isbn?: string;
  minor?: number;
  longId?: string;
  onBeforePrepare?: () => Promise<any>;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
  onDownloadStart?: (info: {
    id: string;
    url: string;
    filename: string;
  }) => void;
}): Promise<{
  data: PDFData;
  type: string;
  error?: ErrorType;
  loading: boolean;
  progress: number;
}> => {
  // Reset state
  let error: ErrorType | undefined;
  let loading = true;
  let progress = 0;
  let type: string | undefined;
  let data: PDFData | undefined;

  try {
    if (props.onBeforePrepare) {
      await props.onBeforePrepare();
    }

    rcLogger.debug('Beginning document processing');

    const headers = new Headers();
    headers.set('accept', '*/*');
    const response = await fetch(props.fileUrl, {
      method: 'HEAD',
      mode: 'cors',
      redirect: 'follow',
      signal: props.signal,
      headers,
    })
      .then((r) => {
        if (!r.ok) {
          rcLogger.error(r);
          throw 'DocumentNotFound';
        }
        const contentLength = parseInt(
          r.headers.get('X-Amzn-Remapped-Content-Length') ||
            r.headers.get('content-length') ||
            '0',
          10
        );

        const canUseRanges =
          !!contentLength &&
          /bytes/.test(
            r.headers.get('accept-ranges') ||
              r.headers.get('x-accept-ranges') ||
              ''
          );
        const useCustom = /bytes/.test(r.headers.get('x-accept-ranges') || '');

        if (useCustom) {
          rcLogger.debug(
            'X-Accept-Ranges header detected. Using custom range headers',
            customRangeHeaders
          );
        }
        const contentType = r.headers.get('content-type');
        const out = {
          contentLength,
          useCustom,
          canUseRanges,
          contentType:
            contentType && contentType.length
              ? getBookTypeFromMime(contentType)
              : ('UNKNOWN' as const),
        };

        return out;
      })
      .catch((e) => {
        rcLogger.warn(e);
        error = { type: 'DownloadError' };
        const out = {
          canUseRanges: false,
          contentLength: 0,
          useCustom: false,
          initialData: undefined,
          contentType: 'UNKNOWN' as const,
        };
        return out;
      });

    if (error) {
      loading = false;
      return { data: undefined, type: undefined, error, loading, progress };
    }

    const bookTypeFromExt = getType(props.fileUrl);
    const bookTypeFromRes = response.contentType;
    const bookType =
      bookTypeFromExt !== 'UNKNOWN' ? bookTypeFromExt : bookTypeFromRes;

    const requiresProcessing = () =>
      !!props.minor && ['2', '3'].indexOf(props.minor.toString()) !== -1;

    if (bookType === 'PDF' && response.canUseRanges) {
      rcLogger.debug('Loading PDF via PDFDataRangeTransport');

      const pdfDataRange = new CustomPDFDataRangeTransport(
        props.fileUrl,
        response.contentLength,
        response.useCustom ? customRangeHeaders : undefined,
        () => {
          error = {
            type: 'DownloadError',
            error: {
              name: '',
              message: 'Failed to download document.',
            },
          };
        },
        () => loading,
        (d, counter) => {
          return processChunk({
            data: d,
            counter,
            onError: (error) => {
              error = error;
            },
            isbn: props.isbn,
            longId: props.longId,
            minor: props.minor,
          });
        },
        (newProgress: number) => {
          progress = newProgress;
          props.onProgress?.(progress);
        }
      );

      data = pdfDataRange;
      type = bookType;
      loading = false;
    } else if (!requiresProcessing() && bookType !== 'EPUB') {
      data = props.fileUrl;
      type = bookType;
      loading = false;
    } else {
      let resData: ArrayBuffer;
      try {
        // Notify that download is starting
        if (props.onDownloadStart) {
          const filename = props.fileUrl.split('/').pop()?.split('?')[0] || 'document';
          const downloadId = `web-${Date.now()}`;
          props.onDownloadStart({
            id: downloadId,
            url: props.fileUrl,
            filename,
          });
        }

        const controller = props.signal ? undefined : new AbortController();
        const { data: responseData } = await axios.get(props.fileUrl, {
          onDownloadProgress: ({
            loaded,
            total,
            lengthComputable,
          }: {
            loaded: number;
            total: number;
            lengthComputable: boolean;
          }) => {
            if (lengthComputable) {
              progress = (loaded / total) * 100;
              props.onProgress?.(progress);
            }
          },
          responseType: 'arraybuffer',
          // Use fetch cancellation if needed; axios v0.x may not support signal natively
          ...(controller ? { signal: controller.signal as any } : {}),
        } as any);
        resData = responseData;
      } catch (e) {
        rcLogger.debug('Download Error', e);
        error = {
          type: 'DownloadError',
          error: e as Error,
        };
        loading = false;
        return { data: undefined, type, error, loading, progress };
      }

      const enhancedData = (await processChunk({
        data: resData,
        isbn: props.isbn,
        longId: props.longId,
        minor: props.minor,
        counter: 0,
        onError: (e) => {
          error = e;
        },
      })) as PDFData;

      data = enhancedData;
      type = bookType;
      loading = false;
    }

    return {
      data,
      type,
      error,
      loading,
      progress,
    };
  } catch (e) {
    rcLogger.error('Error preparing document:', e);
    error = {
      type: 'DownloadError',
      error: e as Error,
    };
    loading = false;
    return {
      data: undefined,
      type,
      error,
      loading,
      progress,
    };
  }
};
